import { Helmet } from "react-helmet";
import { ArrowRight, Brain, Code2, Bot, Globe, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import NavigationButtons from '@/components/NavigationButtons';
import { Link } from "react-router-dom";

const Deepseek = () => {
  return (
    <>
      <Helmet>
        <title>Deepseek - Advanced AI Solutions & Language Models</title>
        <meta 
          name="description" 
          content="Discover Deepseek AI's innovative language models and AI solutions. Learn about our open-source models, development tools, and cutting-edge research in artificial intelligence." 
        />
        <meta 
          name="keywords" 
          content="Deepseek, Deepseek AI, AI language models, open-source AI, AI development, code generation, DeepSeek Coder" 
        />
        <link rel="canonical" href="https://deepseek.ai/deepseek" />
      </Helmet>

      <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <NavigationButtons />
          
          {/* Hero Section */}
          <section className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-deepseek-primary to-deepseek-accent">
              Deepseek
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Pioneering advanced AI solutions with efficient, high-performance language models for developers and enterprises.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/what-is-deepseek-ai">
                <Button variant="outline" className="px-6 py-2 rounded-full">
                  Learn More
                </Button>
              </Link>
            </div>
          </section>

          {/* Main Features Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Why Choose Deepseek?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                  <Brain className="text-deepseek-primary h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">Advanced AI Models</h3>
                <p className="text-gray-600">
                  State-of-the-art language models designed to understand and generate human-like text with remarkable accuracy and efficiency.
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                  <Code2 className="text-deepseek-primary h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">Code Generation</h3>
                <p className="text-gray-600">
                  Specialized models for code understanding and generation, assisting developers in writing better code faster.
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                  <Globe className="text-deepseek-primary h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">Open Source</h3>
                <p className="text-gray-600">
                  Committed to the open-source community, providing access to powerful AI models that can be freely used and modified.
                </p>
              </div>
            </div>
          </section>

          {/* Products Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Deepseek Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Bot className="text-deepseek-primary h-5 w-5 mr-2" />
                  Deepseek R1
                </h3>
                <p className="text-gray-600 mb-4">
                  Our flagship language model designed for general-purpose tasks with exceptional understanding and generation capabilities.
                </p>
                <Link to="/deepseek-r1" className="text-deepseek-primary font-medium flex items-center">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Code2 className="text-deepseek-primary h-5 w-5 mr-2" />
                  Deepseek Coder
                </h3>
                <p className="text-gray-600 mb-4">
                  Specialized model for code generation, understanding, and refactoring across multiple programming languages.
                </p>
                <Link to="/blog/vibe-coding" className="text-deepseek-primary font-medium flex items-center">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Zap className="text-deepseek-primary h-5 w-5 mr-2" />
                  Deepseek Stock
                </h3>
                <p className="text-gray-600 mb-4">
                  AI-powered analysis and insights for the financial market, helping investors make informed decisions.
                </p>
                <Link to="/deepseek-stock" className="text-deepseek-primary font-medium flex items-center">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Bot className="text-deepseek-primary h-5 w-5 mr-2" />
                  AI Chat
                </h3>
                <p className="text-gray-600 mb-4">
                  Interactive chat interface powered by our advanced language models for natural conversations and assistance.
                </p>
                <Link to="/blog/ai-chat" className="text-deepseek-primary font-medium flex items-center">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ Section with Schema */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6 max-w-4xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-medium mb-2">What is Deepseek?</h3>
                <p className="text-gray-600">
                  Deepseek is an AI research company focused on developing cost-efficient, high-performance language models. We provide open-source alternatives to commercial AI models with similar or better capabilities.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-medium mb-2">How does Deepseek compare to other AI models?</h3>
                <p className="text-gray-600">
                  Deepseek offers competitive performance at a lower cost compared to many commercial AI models. Our open-source approach also allows for greater flexibility and customization for specific use cases.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-medium mb-2">Can I use Deepseek for commercial projects?</h3>
                <p className="text-gray-600">
                  Yes, Deepseek's open-source models can be used for commercial projects under the terms of our license. This makes Deepseek an excellent choice for businesses looking to implement AI capabilities.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-medium mb-2">How can I get started with Deepseek?</h3>
                <p className="text-gray-600">
                  You can get started by exploring our models on GitHub, trying our online demos, or integrating our APIs into your applications. Check out our documentation for detailed guides.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center bg-gradient-to-r from-deepseek-primary/10 to-deepseek-accent/10 p-8 sm:p-12 rounded-2xl">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to experience the power of Deepseek AI?</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of developers and businesses already using our models to build the next generation of AI-powered applications.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/deepseek-login">
                <Button className="bg-deepseek-primary hover:bg-deepseek-primary/90 text-white px-8 py-3 rounded-full">
                  Get Started Now
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="px-8 py-3 rounded-full">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Deepseek;
